import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Sparkles, Loader2, Wand2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CATEGORIES } from "@/data/categories";

const EMPTY = {
  title: "", summary: "", body: "", category: "weird-science",
  tags: [], source: "", image_url: "", weirdness: 70, read_time: 2,
  featured: false, published: true,
};

const TONES = ["OddlyTrue Quirky", "Mind-Bending", "Scientific & Dry"];

export const FactFormModal = ({ open, onClose, onSaved, editing }) => {
  const [form, setForm] = useState(EMPTY);
  const [tagsText, setTagsText] = useState("");
  const [saving, setSaving] = useState(false);
  const [aiGenerated, setAiGenerated] = useState(false);

  // AI section
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState(TONES[0]);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (open) {
      const base = editing ? { ...EMPTY, ...editing } : EMPTY;
      setForm(base);
      setTagsText((base.tags || []).join(", "));
      setAiGenerated(editing?.ai_generated || false);
      setTopic("");
    }
  }, [open, editing]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const generate = async () => {
    if (!topic.trim()) {
      toast.error("Enter a topic to generate");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/generate", { topic: topic.trim(), category: form.category, tone });
      setForm((f) => ({
        ...f,
        title: data.title,
        summary: data.summary,
        body: data.body,
        category: data.category,
        source: data.source,
        weirdness: data.weirdness,
        read_time: data.read_time,
        image_url: data.image_url,
      }));
      setTagsText((data.tags || []).join(", "));
      setAiGenerated(true);
      toast.success("AI draft ready — review, tweak, publish", { className: "font-mono" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    if (!form.title.trim() || !form.summary.trim() || !form.body.trim()) {
      toast.error("Title, summary and body are required");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      tags: tagsText.split(",").map((t) => t.trim()).filter(Boolean),
      weirdness: Number(form.weirdness),
      read_time: Number(form.read_time),
    };
    try {
      if (editing) {
        await api.put(`/admin/facts/${editing.id}`, payload);
        toast.success("Fact updated", { className: "font-mono" });
      } else {
        const endpoint = aiGenerated ? "/admin/facts/ai" : "/admin/facts";
        await api.post(endpoint, payload);
        toast.success("Fact created", { className: "font-mono" });
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const field = "w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-ink focus:outline-none focus:border-gold focus:bg-white transition-colors text-sm";
  const lbl = "block font-mono text-xs uppercase tracking-wider text-ink-muted mb-1.5";

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-slate-900/40 backdrop-blur-sm p-4">
      <div data-testid="fact-form-modal" className="w-full max-w-2xl my-8 rounded-3xl border border-slate-200 bg-white shadow-xl">
        <div className="flex items-center justify-between p-6 border-b border-slate-200 sticky top-0 bg-white rounded-t-3xl z-10">
          <h2 className="font-display text-2xl font-bold text-ink">{editing ? "Edit Fact" : "New Fact"}</h2>
          <button data-testid="fact-form-close" onClick={onClose} className="p-2 text-ink-muted hover:text-ink">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* AI Wizard */}
          {!editing && (
            <div className="rounded-2xl border border-gold bg-paper p-4">
              <div className="flex items-center gap-2 mb-3">
                <Wand2 className="w-4 h-4 text-gold" />
                <span className="font-mono text-xs uppercase tracking-widest text-gold">AI Fact Wizard</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  data-testid="ai-topic-input"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="Topic, e.g. deep-sea creatures"
                  className="flex-1 px-4 py-2.5 rounded-xl bg-white border border-gold text-ink focus:outline-none focus:border-gold transition-colors text-sm"
                />
                <select data-testid="ai-tone-select" value={tone} onChange={(e) => setTone(e.target.value)} className="sm:w-48 px-4 py-2.5 rounded-xl bg-white border border-gold text-ink focus:outline-none focus:border-gold text-sm">
                  {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <button
                  data-testid="ai-generate-fact-button"
                  onClick={generate}
                  disabled={generating}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-ink text-white font-mono text-sm disabled:opacity-60 whitespace-nowrap hover:bg-gold hover:text-ink transition-colors"
                >
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Generate
                </button>
              </div>
            </div>
          )}

          <div>
            <label className={lbl}>Title</label>
            <input data-testid="fact-form-title" value={form.title} onChange={(e) => set("title", e.target.value)} className={field} />
          </div>
          <div>
            <label className={lbl}>Summary</label>
            <textarea data-testid="fact-form-summary" value={form.summary} onChange={(e) => set("summary", e.target.value)} rows={2} className={field} />
          </div>
          <div>
            <label className={lbl}>Body (blank line between paragraphs)</label>
            <textarea data-testid="fact-form-body" value={form.body} onChange={(e) => set("body", e.target.value)} rows={7} className={field} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Category</label>
              <select data-testid="fact-form-category" value={form.category} onChange={(e) => set("category", e.target.value)} className={field}>
                {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Tags (comma separated)</label>
              <input data-testid="fact-form-tags" value={tagsText} onChange={(e) => setTagsText(e.target.value)} className={field} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Weirdness ({form.weirdness})</label>
              <input type="range" min={0} max={100} value={form.weirdness} onChange={(e) => set("weirdness", e.target.value)} className="w-full accent-gold" />
            </div>
            <div>
              <label className={lbl}>Read time (min)</label>
              <input type="number" min={1} value={form.read_time} onChange={(e) => set("read_time", e.target.value)} className={field} />
            </div>
          </div>
          <div>
            <label className={lbl}>Source</label>
            <input data-testid="fact-form-source" value={form.source} onChange={(e) => set("source", e.target.value)} className={field} />
          </div>
          <div>
            <label className={lbl}>Image URL (optional)</label>
            <input value={form.image_url} onChange={(e) => set("image_url", e.target.value)} className={field} placeholder="Leave blank for a curated image" />
          </div>
          <div className="flex flex-wrap gap-6 pt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input data-testid="fact-form-published" type="checkbox" checked={form.published} onChange={(e) => set("published", e.target.checked)} className="w-4 h-4 accent-gold" />
              <span className="font-mono text-sm text-ink">Published</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input data-testid="fact-form-featured" type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="w-4 h-4 accent-gold" />
              <span className="font-mono text-sm text-ink">Feature on homepage</span>
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-slate-200">
          <button onClick={onClose} className="px-5 py-2.5 rounded-xl border border-slate-300 text-ink-muted font-mono text-sm hover:border-slate-400">
            Cancel
          </button>
          <button data-testid="fact-form-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-ink text-white font-mono text-sm disabled:opacity-60 hover:bg-gold hover:text-ink transition-colors">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {editing ? "Save Changes" : "Create Fact"}
          </button>
        </div>
      </div>
    </div>
  );
};
